import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import { useUploadAttendanceSheet } from "../../../api/queries/learningQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { downloadTextFile, toCsv } from "../../../utils/csv";
import { seminarApi } from "../../../api/learningApi";
import { saveFile } from "../../../api/adminToolsApi";
import { PARTICIPANT_STATUS, titleCase } from "./learningFormat";

const EMPTY = [];
const MAX_MB = 10;
const count = (v) => (Array.isArray(v) ? v.length : Number(v) || 0);
const statusLabel = (s) =>
  PARTICIPANT_STATUS[s]?.label || titleCase(s || "") || "—";

/** Codes list (not participants / duplicates) as chips. */
const CodeList = ({ title, codes, tone = "amber", hint }) =>
  codes.length === 0 ? null : (
    <div
      className={`alert alert-${tone === "red" ? "danger" : "warning"} fs-080 py-2 mb-2`}
    >
      <div className="fw-semibold mb-1">
        {title} ({codes.length})
      </div>
      {hint && <div className="mb-1">{hint}</div>}
      <div className="d-flex flex-wrap gap-1">
        {codes.map((c, i) => (
          <span
            key={`${c}-${i}`}
            className="chip chip--sm chip--alt font-monospace"
          >
            {typeof c === "string" ? c : c.code}
          </span>
        ))}
      </div>
    </div>
  );

/**
 * Upload a filled attendance sheet (Excel/CSV): preview what would change,
 * then apply. Template columns: Employee ID, Name, Status
 * (Present / Absent / Excused; blank = present).
 */
const AttendanceSheetDialog = ({ seminar, onClose }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const upload = useUploadAttendanceSheet();

  // The server's .xlsx (seminar details + participants; it also checks the
  // sheet belongs to this seminar on upload); the CSV below is the fallback
  const [downloading, setDownloading] = useState(false);
  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      saveFile(await seminarApi.attendanceTemplate(seminar.id));
    } catch {
      toast.info("Downloaded a simple CSV template instead.");
      downloadCsvTemplate();
    } finally {
      setDownloading(false);
    }
  };
  const downloadCsvTemplate = () => {
    const rows = (seminar.participants || EMPTY).map((p) => [
      p.employeeNumber || "",
      p.name || "",
      "",
    ]);
    const safe = (seminar.title || "seminar")
      .replace(/[^\w-]+/g, "_")
      .slice(0, 40);
    downloadTextFile(
      toCsv(["Employee ID", "Name", "Status"], rows),
      `attendance_${safe}.csv`,
    );
  };

  const pick = (f) => {
    setPreview(null);
    setError(null);
    if (f && f.size > MAX_MB * 1024 * 1024) {
      setError(`The file is larger than ${MAX_MB} MB.`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setFile(f || null);
  };

  const run = (apply) =>
    upload.mutate(
      { id: seminar.id, file, apply },
      {
        onSuccess: (res) => {
          setError(null);
          if (apply) {
            toast.success(
              `Attendance updated for ${res.updated ?? count(res.changes)} participant(s).`,
            );
            onClose();
          } else setPreview(res);
        },
        onError: (err) => {
          const m = err?.response?.data?.message;
          setError(
            Array.isArray(m)
              ? m.join(" ")
              : getApiErrorMessage(err, "Could not read the sheet."),
          );
        },
      },
    );

  const changes = preview?.changes || EMPTY;
  const notParticipants = preview?.notParticipants || EMPTY;
  const duplicates = preview?.duplicates || EMPTY;
  const badStatus = preview?.badStatus || EMPTY;
  const notOnSheet = preview?.notOnSheet || EMPTY;

  return (
    <Dialog
      title={`Upload attendance — ${preview?.seminar?.title || seminar.title}`}
      subtitle="Fill in the template, then upload it to preview the changes."
      size="lg"
      onClose={onClose}
      busy={upload.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={upload.isPending}
          >
            Close
          </button>
          {preview ? (
            <button
              type="button"
              className="btn btn-success profile-btn"
              onClick={() => run(true)}
              disabled={upload.isPending || changes.length === 0}
            >
              {upload.isPending && (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                ></span>
              )}
              {changes.length
                ? `Apply ${changes.length} change${changes.length === 1 ? "" : "s"}`
                : "No changes to apply"}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary profile-btn"
              onClick={() => run(false)}
              disabled={upload.isPending || !file}
            >
              {upload.isPending && (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                ></span>
              )}
              Preview
            </button>
          )}
        </>
      }
    >
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <button
          type="button"
          className="btn btn-sm btn-light border"
          onClick={downloadTemplate}
          disabled={downloading}
        >
          <i className="bi bi-file-earmark-excel me-1"></i>
          {downloading ? "Preparing…" : "Download template"}
        </button>
        <span className="fs-080 text-slate-600">
          Pre-filled with this seminar&apos;s participants. Status: Present /
          Absent / Excused (blank = present).
        </span>
      </div>
      <input
        ref={fileRef}
        type="file"
        className="form-control mb-2"
        accept=".xlsx,.xls,.csv"
        onChange={(e) => pick(e.target.files?.[0])}
        disabled={upload.isPending}
        aria-label="Attendance sheet"
      />
      {error && <div className="alert alert-danger fs-085 py-2">{error}</div>}

      {preview && (
        <div className="mt-2">
          <div className="learn-totals mb-2">
            <div className="learn-total">
              <div className="approval-meta-label">Rows read</div>
              <strong>{preview.rowsRead ?? 0}</strong>
            </div>
            <div className="learn-total">
              <div className="approval-meta-label">Changes</div>
              <strong>{changes.length}</strong>
            </div>
            <div className="learn-total">
              <div className="approval-meta-label">Unchanged</div>
              <strong>{count(preview.unchanged)}</strong>
            </div>
            <div className="learn-total">
              <div className="approval-meta-label">Not on sheet</div>
              <strong>{notOnSheet.length}</strong>
            </div>
          </div>
          {preview.columns && (
            <p className="fs-075 text-slate-500 mb-2">
              Read from header row {preview.columns.headerRow}: ID in column{" "}
              {preview.columns.idColumn}, status in column{" "}
              {preview.columns.statusColumn}.
            </p>
          )}

          {changes.length > 0 ? (
            <div className="table-responsive mb-2">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Employee ID</th>
                    <th>Name</th>
                    <th>Now</th>
                    <th>After</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c) => (
                    <tr key={c.code}>
                      <td className="font-monospace">{c.code}</td>
                      <td>{c.name}</td>
                      <td className="text-slate-500">
                        {statusLabel(c.current)}
                      </td>
                      <td className="fw-semibold">{statusLabel(c.next)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-info fs-080 py-2">
              Nothing to change: the sheet matches the current attendance.
            </div>
          )}

          <CodeList
            title="Not participants of this seminar"
            codes={notParticipants}
            hint="These rows are ignored."
          />
          <CodeList
            title="Listed more than once"
            codes={duplicates}
            hint="Only the first row is used."
          />
          {badStatus.length > 0 && (
            <div className="alert alert-danger fs-080 py-2 mb-2">
              <div className="fw-semibold mb-1">
                Unknown status ({badStatus.length})
              </div>
              {badStatus.map((b, i) => (
                <div key={i}>
                  <span className="font-monospace">{b.code}</span>: “{b.value}”
                  (use Present, Absent or Excused)
                </div>
              ))}
            </div>
          )}
          {notOnSheet.length > 0 && (
            <div className="alert alert-secondary fs-080 py-2 mb-0">
              <div className="fw-semibold mb-1">
                Participants not on the sheet ({notOnSheet.length}): left as
                they are
              </div>
              {notOnSheet.map((p) => (
                <div key={p.code}>
                  <span className="font-monospace">{p.code}</span> {p.name} ·{" "}
                  {statusLabel(p.status)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default AttendanceSheetDialog;
